import React from 'react';
import { Box, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { InfoOutlined as InfoIcon, ReportProblemOutlined as WarningIcon } from '@material-ui/icons';
import useStyle from './TimeElements.style';
import { colors } from 'theme/color-palette';
import rowImage from 'assets/svgs/mts_row.svg';
import columnImage from 'assets/svgs/mts_column.svg';
import timeAcrossImage from 'assets/svgs/mts_time_across.svg';
import { setPivotOptions, setSelectedList } from '../Preview.actions';
import { isMultiWeekSel, isCalFamily } from 'components/Preview/filter/FilterCondition';
import { findAppliedTimeFilterCountForPivot, calculateTotalColumnCount } from '../Preview.utils';
import {
	PIVOT_MTS_VALUE,
	TIME_ACROSS_MTS_VALUE,
	PIVOT_COLUMN_LIMIT,
	MULTI_TIME_SELECTION,
	ENTERPRISE_INVENTORY,
	PIVOT_SINGLE_TIME_VALUES
} from 'constants/constant';
import { deepClone } from 'helper/helper';

const TimeRangeSelection = () => {
	const classes = useStyle();
	const dispatch = useDispatch();
	const { templateData, selectedTemplate } = useSelector((state) => state.templateReducer);
	const { selectedList, filterList, pivotOpts } = useSelector((state) => state.previewReducer);

	const pivotColumn = pivotOpts?.pivotColumn || '';

	if (pivotOpts && !pivotOpts.showPopup) {
		return null;
	}
	/**
	 * Find out which single time filter has applied
	 * If undefined, that means Pivot in on MTS
	 */
	const appliedTimeFilter = filterList.find(
		(filter) =>
			filter?.field &&
			filter?.localName !== MULTI_TIME_SELECTION &&
			PIVOT_SINGLE_TIME_VALUES.includes(filter?.technicalName)
	);
	const timeAttributeUsedForPivot = appliedTimeFilter?.technicalName || PIVOT_MTS_VALUE;
	const timeAttributeUsedForTimeAcross = appliedTimeFilter?.technicalName || TIME_ACROSS_MTS_VALUE;
	// Finds the applied time filter count
	const appliedTimeFilterCount = findAppliedTimeFilterCountForPivot(filterList);

	let selectionBoxStyle = [{}, {}, {}];
	let bgColor1 = colors.WHiTE;
	let bgColor2 = colors.WHiTE;
	let bgColor3 = colors.WHiTE;
	let showLimitExceededPivotColumn = false;
	/**
	 * Show info message
	 * if no time attribute is selected, or
	 * if only one time attribute is selected in the case of MTS
	 */
	let showFilterMessage = appliedTimeFilterCount === 0;
	if (appliedTimeFilterCount === 1 && timeAttributeUsedForPivot === PIVOT_MTS_VALUE) {
		showFilterMessage = true;
	}
	/**
	 * Finds the total no. of Table columns
	 * & show Info message if it exceeds the limit
	 */
	const totalColumCountPivot = calculateTotalColumnCount(selectedList, filterList, false);

	const totalColumnCountTimeAcross = calculateTotalColumnCount(selectedList, filterList, true);
	if (totalColumCountPivot > PIVOT_COLUMN_LIMIT) {
		showLimitExceededPivotColumn = true;
	}

	const showBanner = showFilterMessage || showLimitExceededPivotColumn;
	const showLimitExceededTimeAcross = totalColumnCountTimeAcross > PIVOT_COLUMN_LIMIT;
	const showBannerTimeAcross = showFilterMessage || showLimitExceededTimeAcross;

	const onSelection = (mode, value) => {
		const isDefaultSelection = mode === 'default';
		const isTimeAcross = mode === 'timeAcross';
		if (isTimeAcross) {
			if (showBannerTimeAcross) return;
		} else if (!isDefaultSelection && showBanner) {
			return;
		}
		/**
		 * This flag `pivotOpts.isMultiWeek` is set to true in below cases,
		 * - User does multiple weeks selections from Walmart Calendar > Week filter option, or
		 * - User does Pivot on MTS
		 * otherwise always set to false
		 *
		 * Finally used this flag to decide which pivotColumn value has to send to BE
		 */
		if (!isDefaultSelection) {
			pivotOpts.isMultiWeek = appliedTimeFilter
				? isMultiWeekSel(appliedTimeFilter, Number(selectedTemplate))
				: false;
			if (value === PIVOT_MTS_VALUE) {
				pivotOpts.isMultiWeek = true;
			}
		}
		pivotOpts.isTimeAcross = isTimeAcross;
		pivotOpts.showPopup = false;
		pivotOpts.pivotColumn = value;
		dispatch(setPivotOptions({ ...pivotOpts }));

		if (isDefaultSelection) {
			// Find the Time column
			const calFamilyColumnIndex = selectedList?.findIndex((column) => isCalFamily(column));

			// If the calendar column is at the bottom of the selected list, then move that column to the top
			// Note: This is required because the order of columns is messing up the order of columns in the excel sheet report.
			if (calFamilyColumnIndex > 0) {
				const selectedListClone = deepClone(selectedList);
				const calFamilyColumnArray = selectedListClone.splice(calFamilyColumnIndex, 1);
				const newSelectedList = [...calFamilyColumnArray, ...selectedListClone];
				dispatch(setSelectedList(newSelectedList));
			}
			return;
		}
		/**
		 * On applying Pivot on any one of the Single Time attributes,
		 * it should be added to the selectedList and all other Time attributes should be removed.
		 *
		 * (Pivot on MTS scenario is already handled)
		 */
		if (PIVOT_SINGLE_TIME_VALUES.includes(value)) {
			const currentTemplate = templateData?.find(
				(e) => Number(e?.templateId) === Number(selectedTemplate)
			);
			if (currentTemplate) {
				// Attributes under 'Walmart Calendar'
				const parent = 'Walmart Calendar';
				const timeAttributeParentObj = currentTemplate?.time?.find(
					(e) => e?.businessName === parent
				);
				const timeAttributeObj =
					timeAttributeParentObj &&
					timeAttributeParentObj?.subAttributes?.find((e) => e?.technicalName === value);
				// Remove other time attributes under Walmart Calendar
				const newSelectedList = selectedList.filter((e) => e?.parent !== parent);
				// Adding the new selected time attribute aka Pivoted attribute to the selectedList
				if (timeAttributeObj) {
					newSelectedList.unshift({ ...timeAttributeObj, parent });
				}
				/**
				 * Hourly Store Inventory template(OR EI)
				 * While Pivot on Hour attribute, that attribute(snap_shot_hour) should be added to the selectedList
				 */
				if (ENTERPRISE_INVENTORY.includes(Number(currentTemplate?.templateId))) {
					const snapShotHour = 'snap_shot_hour';
					const hourAttributeObj = currentTemplate?.dimensions?.find(
						(e) => e?.technicalName === snapShotHour
					);
					const hasAddedAlready = newSelectedList.some(
						(item) => item?.technicalName === snapShotHour
					);
					if (!hasAddedAlready) {
						newSelectedList.unshift({ ...hourAttributeObj, parent: 'dimensions' });
					}
				}

				dispatch(setSelectedList([...newSelectedList]));
			}
		}
	};

	bgColor1 = pivotColumn === 'default' ? '#F8F8F8' : bgColor1;
	bgColor2 =
		!pivotOpts?.isTimeAcross && [PIVOT_MTS_VALUE, ...PIVOT_SINGLE_TIME_VALUES].includes(pivotColumn)
			? '#F8F8F8'
			: bgColor2;
	bgColor3 = pivotOpts?.isTimeAcross ? '#F8F8F8' : bgColor3;

	if (showLimitExceededTimeAcross || showFilterMessage) {
		bgColor1 = colors.WHiTE;
		bgColor2 = colors.WHiTE;
		bgColor3 = colors.WHiTE;
		selectionBoxStyle = [
			//default
			{ opacity: '0.5', border: 'none', cursor: 'not-allowed', padding: '16px' },
			//pivot column
			{ opacity: '0.5', border: 'none', cursor: 'not-allowed', padding: '16px' },
			//time across
			{ opacity: '0.5', border: 'none', cursor: 'not-allowed', padding: '16px' }
		];
	} else if (showLimitExceededPivotColumn) {
		bgColor1 = colors.WHiTE;
		bgColor2 = colors.WHiTE;
		selectionBoxStyle = [
			//default
			{},
			//pivot column
			{ opacity: '0.5', border: 'none', cursor: 'not-allowed', padding: '16px' },
			//time across
			{}
		];
	}

	return (
		<Box className={classes.TimeRangeSelectionContainer}>
			<Box>
				<Typography className={classes.TimeRangeSelectionTitle}>{'Format time ranges'}</Typography>
				<Typography className={classes.TimeRangeSelectionContent}>
					{'You can configure time ranges into either rows or columns'}
				</Typography>
			</Box>
			{/* Information Banner */}
			{showBanner ? (
				<InformationBanner
					showFilterMessage={showFilterMessage}
					showLimitExceededPivotColumn={showLimitExceededPivotColumn}
					showLimitExceededTimeAcross={showLimitExceededTimeAcross}
				/>
			) : null}
			{/* Time Range Selection */}
			{/* Default */}
			<Box
				className={classes.TimeRangeSelection}
				onClick={() => onSelection('default')}
				id="show-default"
				style={{
					...selectionBoxStyle[0],
					backgroundColor: bgColor1
				}}>
				<Box>
					<Typography>{'Show time ranges vertically in rows'}</Typography>
					<Box className={classes.defaultSelection}>{'Default'}</Box>
				</Box>
				<img src={rowImage} />
			</Box>
			{/* Column */}
			<Box
				className={classes.TimeRangeSelection}
				onClick={() => onSelection('pivotColumn', timeAttributeUsedForPivot)}
				style={{
					...selectionBoxStyle[1],
					backgroundColor: bgColor2
				}}>
				<Box>
					<Typography>{'Show time ranges horizontally across columns'}</Typography>
				</Box>
				<img src={columnImage} />
			</Box>
			{/* Time Cross */}
			<Box
				className={classes.TimeRangeSelection}
				onClick={() => onSelection('timeAcross', timeAttributeUsedForTimeAcross)}
				style={{
					...selectionBoxStyle[2],
					backgroundColor: bgColor3
				}}>
				<Box>
					<Typography>{'Show time ranges across columns and metrics in rows'}</Typography>
				</Box>
				<img src={timeAcrossImage} />
			</Box>
		</Box>
	);
};

const InformationBanner = ({
	showFilterMessage,
	showLimitExceededPivotColumn,
	showLimitExceededTimeAcross
}) => {
	let content = null;
	const classes = useStyle();
	if (showFilterMessage) {
		content = (
			<>
				<InfoIcon fontSize="small" />
				<Typography className={classes.infoText}>
					{'Please select the time period and filter with time ranges first.'}
				</Typography>
			</>
		);
	}
	if (showLimitExceededTimeAcross) {
		content = (
			<>
				<WarningIcon fontSize="small" />
				<Typography className={classes.infoText}>
					{`Columns will exceed limit of ${PIVOT_COLUMN_LIMIT}. Please reduce the data or time ranges selection.`}
				</Typography>
			</>
		);
	} else if (showLimitExceededPivotColumn) {
		content = (
			<>
				<WarningIcon fontSize="small" />
				<Typography className={classes.infoText}>
					{`Horizontal Time Range is disabled because columns will exceed limit of ${PIVOT_COLUMN_LIMIT}. Please reduce the data or time ranges selection.`}
				</Typography>
			</>
		);
	}

	const showPivotColumnExceeded = showLimitExceededPivotColumn || showLimitExceededTimeAcross;
	return (
		<Box
			className={classes.infoContainer}
			style={{ backgroundColor: showPivotColumnExceeded ? '#FFF9E9' : '#F8F8F8' }}>
			{content}
		</Box>
	);
};

export default TimeRangeSelection;
